import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { Marca } from '@selflevel/ui';
import { useAreaMap, useFocusSessions, useHabits, useSettings } from '../store/hooks';
import { updateSettings } from '../store/mutations';
import { Card, Empty, Ring, Segmented, fechaCorta, num } from '../ui/primitivas';
import { formatClock, useFocus } from './foco-estado';
import {
  acotaAjuste,
  AJUSTES_CICLO,
  minutosDeDescanso,
  type AjusteCiclo,
  type TipoDescanso,
} from './foco-ciclo';

import {
  acotaMinutos,
  casillaMarcada,
  DURACIONES,
  eligeDuracion,
  MAX_MINUTOS,
  MIN_MINUTOS,
  OTRA,
  PASO_MINUTOS,
  puntosPrevistos,
} from './foco-duracion';

/**
 * FOCO — el pomodoro.
 *
 * Tres decisiones que lo separan de un temporizador cualquiera:
 *
 *  1. **El tiempo se mide restando marcas de tiempo, no sumando tics.** Un
 *     `setInterval` en una pestaña de fondo se ralentiza o se para; un
 *     temporizador que pierde minutos al bloquear el móvil está roto.
 *  2. **La sesión se guarda aunque se corte.** Una sesión interrumpida vale 0
 *     puntos —si valiera algo, la forma óptima de subir sería arrancar y
 *     cortar— pero **se guarda igual**, porque pasó y el historial es del
 *     usuario, no del marcador.
 *  3. **Nada interrumpe una sesión en marcha.** Hitos, cruces de tramo y cierres
 *     de temporada se encolan y esperan al final.
 */
export function Foco(): ReactElement {
  const settings = useSettings();
  const habits = useHabits();
  const areas = useAreaMap();
  const sesiones = useFocusSessions(12);
  const focus = useFocus();
  /** Toda la lógica de la duración vive en `foco-duracion.ts`, que se prueba.
   *  Arranca con el ajuste libre abierto si lo guardado no es un preset: quien
   *  dejó puestos noventa minutos vuelve a encontrarlos. */
  const [duracion, setDuracion] = useState(() => ({
    minutos: acotaMinutos(settings.focusDefaultMinutes),
    libre: !(DURACIONES as readonly number[]).includes(settings.focusDefaultMinutes),
  }));
  const minutos = duracion.minutos;
  const esPreset = !duracion.libre;

  /**
   * EL BORRADOR DEL CAMPO. Existe por un fallo que solo aparece tecleando: el
   * campo es controlado, así que acotar en cada pulsación impide escribir «10»
   * —al teclear el «1» se acota a 5 y el «0» ya no llega—. Mientras el campo
   * tiene el foco manda la cadena tal cual; al salir se acota y se guarda.
   */
  const [borrador, setBorrador] = useState<string | null>(null);

  /**
   * La duración elegida SE GUARDA. `focusDefaultMinutes` ya existía en ajustes
   * y no lo escribía nadie: quien trabaja en bloques de noventa minutos tenía
   * que volver a marcarlos cada vez que abría la pantalla, que es exactamente
   * la clase de peaje que hace que una función configurable no se use.
   */
  function fijaDuracion(siguiente: { minutos: number; libre: boolean }) {
    setDuracion(siguiente);
    if (siguiente.minutos !== duracion.minutos) {
      void updateSettings({ focusDefaultMinutes: siguiente.minutos });
    }
  }

  function cambiarMinutos(n: number) {
    fijaDuracion({ minutos: acotaMinutos(n, duracion.minutos), libre: duracion.libre });
  }

  // Una tanda de por la mañana no sigue por la tarde (ver `TANDA_CADUCA_MS`).
  useEffect(() => {
    useFocus.getState().revisarTanda();
  }, []);

  // Un solo intervalo, y solo repinta. La verdad es `endsAt - Date.now()`.
  useEffect(() => {
    if (focus.phase !== 'running') return;
    const id = setInterval(() => focus.tick(), 250);
    return () => clearInterval(id);
  }, [focus]);

  // Cerrar la sesión a su hora y guardarla no se hace aquí: lo hace `VigiaFoco`,
  // montado en el armazón, para que no dependa de que esta pantalla esté abierta.

  const corriendo = focus.phase === 'running' || focus.phase === 'paused';
  const total = focus.plannedMinutes * 60_000;
  // `remainingMs` lo mantiene al día el `tick`. Llamar a `Date.now()` aquí haría
  // el render impuro: dos renders seguidos con el mismo estado darían números
  // distintos, y eso rompe cualquier razonamiento sobre la interfaz.
  const restante = focus.remainingMs;
  const enDescanso = corriendo && focus.tipo === 'descanso';
  const porCiclo = settings.focusCyclesBeforeLong;

  function empezarDescanso(tipo: TipoDescanso) {
    focus.startBreak(tipo, minutosDeDescanso(tipo, settings));
  }

  return (
    <main className="screen">
      <header className="topbar glass-2">
        <h1 className="t-title-3">Foco</h1>
        {enDescanso ? (
          <span className="chip">Descanso</span>
        ) : corriendo ? (
          <span className="chip chip--accent">En marcha</span>
        ) : null}
      </header>

      <section
        className={enDescanso ? 'foco-reloj foco-reloj--descanso' : 'foco-reloj'}
        style={{ display: 'grid', placeItems: 'center', gap: 'var(--sp-5)' }}
      >
        <Ring
          size={220}
          progress={corriendo ? 1 - restante / total : 0}
          value={
            <span className="t-display tnum" style={{ fontSize: 56 }}>
              {corriendo ? formatClock(restante) : `${minutos}:00`}
            </span>
          }
          label="Temporizador"
        />

        <PuntosDeTanda ciclo={focus.ciclo} porCiclo={porCiclo} />

        {enDescanso ? (
          <div className="stack stack--tight" style={{ alignItems: 'center' }}>
            <p className="t-title-3">{focus.label}</p>
            <p className="t-sm t-dim">Levántate, mira lejos, bebe agua. No cuenta como foco.</p>
          </div>
        ) : corriendo ? (
          <p className="t-title-3">{focus.label || 'Foco'}</p>
        ) : (
          <div className="stack" style={{ alignItems: 'center' }}>
            {/* El segmentado siempre tiene una casilla marcada: si los minutos no
                son uno de los tres presets, la marcada es «Otra». Sin esto, un
                valor de 32 dejaría la pieza en la primera casilla y el control
                diría «15» mientras el reloj dice otra cosa. */}
            <Segmented
              label="Duración"
              value={casillaMarcada(duracion)}
              onChange={(v) => fijaDuracion(eligeDuracion(duracion, v))}
              options={[
                ...DURACIONES.map((d) => ({ value: String(d), label: `${d} min` })),
                { value: OTRA, label: 'Otra' },
              ]}
            />

            {esPreset ? null : (
              <div className="paso-min" role="group" aria-label="Minutos de la sesión">
                <button
                  type="button"
                  className="btn btn--bare paso-min__lado"
                  onClick={() => cambiarMinutos(minutos - PASO_MINUTOS)}
                  disabled={minutos <= MIN_MINUTOS}
                  aria-label={`Quitar ${PASO_MINUTOS} minutos`}
                >
                  −
                </button>
                {/* Un `input` de verdad y no solo los dos botones: pasar de 25 a
                    180 a saltos de cinco son treinta y un toques. Con teclado
                    numérico en móvil (`inputMode`), es uno. */}
                <label className="paso-min__campo">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={MIN_MINUTOS}
                    max={MAX_MINUTOS}
                    value={borrador ?? minutos}
                    style={
                      {
                        '--cifras': Math.max(1, String(borrador ?? minutos).length),
                      } as CSSProperties
                    }
                    onChange={(e) => setBorrador(e.target.value)}
                    onBlur={() => {
                      if (borrador !== null) cambiarMinutos(Number(borrador));
                      setBorrador(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    aria-label="Minutos"
                  />
                  <span className="t-sm t-dim">min</span>
                </label>
                <button
                  type="button"
                  className="btn btn--bare paso-min__lado"
                  onClick={() => cambiarMinutos(minutos + PASO_MINUTOS)}
                  disabled={minutos >= MAX_MINUTOS}
                  aria-label={`Añadir ${PASO_MINUTOS} minutos`}
                >
                  +
                </button>
              </div>
            )}

            {/* Lo que cambia con la duración, dicho donde se decide y no en la
                nota del final: el baremo no es lineal y una sesión de nueve
                minutos no cuenta. Enterarse después de haberla hecho es peor. */}
            <p className="t-caption t-dim">{puntosPrevistos(minutos)} puntos al terminarla</p>
          </div>
        )}
      </section>

      {corriendo ? (
        <div className="row">
          <button
            type="button"
            className="btn btn--block"
            onClick={() => (focus.phase === 'running' ? focus.pause() : focus.resume())}
          >
            {focus.phase === 'running' ? 'Pausa' : 'Seguir'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => focus.finish()}
          >
            {enDescanso ? 'Terminar descanso' : 'Terminar'}
          </button>
        </div>
      ) : (
        <>
          {focus.oferta ? (
            <OfertaDescanso
              tipo={focus.oferta}
              ciclo={focus.ciclo}
              porCiclo={porCiclo}
              minutos={minutosDeDescanso(focus.oferta, settings)}
              onEmpezar={() => {
                if (focus.oferta) empezarDescanso(focus.oferta);
              }}
              onSaltar={() => focus.skipBreak()}
            />
          ) : null}
          <p className="t-sm t-dim">¿Sobre qué?</p>
          <div className="stack stack--tight">
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() =>
                focus.start({ plannedMinutes: minutos, label: 'Foco', habitId: null, areaId: null })
              }
            >
              Empezar sin más
            </button>
            {(habits ?? []).slice(0, 6).map((habit) => {
              const area = areas.get(habit.areaId);
              return (
                <button
                  key={habit.id}
                  type="button"
                  className="habit"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    ['--area' as string]: `var(${area?.colorToken ?? '--dim-1'})`,
                  }}
                  onClick={() =>
                    focus.start({
                      plannedMinutes: minutos,
                      label: habit.name,
                      habitId: habit.id,
                      areaId: habit.areaId,
                    })
                  }
                >
                  <span className="habit__disc">
                    <Marca id={habit.marca} size={22} polarity="area" />
                  </span>
                  <span className="grow habit__name">{habit.name}</span>
                  <span className="t-sm t-dim">{minutos} min</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {corriendo ? null : (
        <Card title="Pomodoros y descansos">
          <div className="stack">
            <AjusteDelCiclo
              cual="corto"
              titulo="Descanso corto"
              valor={settings.focusBreakMinutes}
              unidad="min"
              onCambio={(n) => void updateSettings({ focusBreakMinutes: n })}
            />
            <AjusteDelCiclo
              cual="largo"
              titulo="Descanso largo"
              valor={settings.focusLongBreakMinutes}
              unidad="min"
              onCambio={(n) => void updateSettings({ focusLongBreakMinutes: n })}
            />
            <AjusteDelCiclo
              cual="porCiclo"
              titulo="Largo cada"
              valor={porCiclo}
              unidad="pomodoros"
              onCambio={(n) => void updateSettings({ focusCyclesBeforeLong: n })}
            />
            <p className="t-caption t-dim">
              Tras cada pomodoro completo te ofrezco el descanso que toca. Los descansos no suman
              puntos: sirven para que el siguiente pomodoro también cuente.
            </p>
          </div>
        </Card>
      )}

      <Card title="Últimas sesiones">
        {sesiones === undefined || sesiones.length === 0 ? (
          <Empty title="Todavía ninguna">
            La duración la pones tú. Por debajo de diez minutos no suma; a partir de cuarenta y
            cinco, el doble.
          </Empty>
        ) : (
          <ul className="stack stack--tight">
            {sesiones.map((sesion) => (
              <li key={sesion.id} className="row row--between" style={{ minHeight: 44 }}>
                <span className="grow t-sm">{sesion.label}</span>
                <span className="t-sm t-dim tnum">
                  {fechaCorta(sesion.localDate)} · {num(sesion.actualMinutes)} min
                  {sesion.interrupted ? ' · cortada' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </main>
  );
}

/** La tanda en puntos: los llenos son pomodoros completos; el largo llega al llenarlos. */
function PuntosDeTanda({ ciclo, porCiclo }: { ciclo: number; porCiclo: number }): ReactElement {
  const hechos = Math.min(ciclo, porCiclo);
  return (
    <div
      className="foco-tanda"
      role="img"
      aria-label={`${hechos} de ${porCiclo} pomodoros de la tanda`}
    >
      {Array.from({ length: porCiclo }, (_, i) => (
        <span key={i} className="foco-tanda__punto" data-hecho={i < hechos} />
      ))}
    </div>
  );
}

function OfertaDescanso({
  tipo,
  ciclo,
  porCiclo,
  minutos,
  onEmpezar,
  onSaltar,
}: {
  tipo: TipoDescanso;
  ciclo: number;
  porCiclo: number;
  minutos: number;
  onEmpezar: () => void;
  onSaltar: () => void;
}): ReactElement {
  return (
    <div className="card foco-oferta stack">
      <div>
        <p className="t-body-strong">
          {tipo === 'largo'
            ? `Tanda completa: ${num(porCiclo)} pomodoros`
            : `Pomodoro ${num(ciclo)} de ${num(porCiclo)} hecho`}
        </p>
        <p className="t-sm t-dim">
          Toca un descanso {tipo === 'largo' ? 'largo' : 'corto'} de {num(minutos)} min.
        </p>
      </div>
      <div className="row">
        <button type="button" className="btn btn--primary btn--block" onClick={onEmpezar}>
          Descansar
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={onSaltar}>
          Saltar
        </button>
      </div>
    </div>
  );
}

/**
 * Un ajuste del ciclo: −/+ y el número escrito a mano. Mismo borrador que la
 * duración del foco: acotar en cada pulsación impediría escribir «10».
 */
function AjusteDelCiclo({
  cual,
  titulo,
  valor,
  unidad,
  onCambio,
}: {
  cual: AjusteCiclo;
  titulo: string;
  valor: number;
  unidad: string;
  onCambio: (n: number) => void;
}): ReactElement {
  const { min, max, paso } = AJUSTES_CICLO[cual];
  const [borrador, setBorrador] = useState<string | null>(null);
  const fijar = (n: number) => {
    const acotado = acotaAjuste(cual, n, valor);
    if (acotado !== valor) onCambio(acotado);
  };
  return (
    <div className="row row--between foco-ajuste">
      <span className="t-sm">{titulo}</span>
      <div className="paso-min" role="group" aria-label={titulo}>
        <button
          type="button"
          className="btn btn--bare paso-min__lado"
          onClick={() => fijar(valor - paso)}
          disabled={valor <= min}
          aria-label={`Menos: ${titulo}`}
        >
          −
        </button>
        <label className="paso-min__campo">
          <input
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={borrador ?? valor}
            style={{ '--cifras': Math.max(1, String(borrador ?? valor).length) } as CSSProperties}
            onChange={(e) => setBorrador(e.target.value)}
            onBlur={() => {
              if (borrador !== null) fijar(Number(borrador));
              setBorrador(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            aria-label={titulo}
          />
          <span className="t-sm t-dim">{unidad}</span>
        </label>
        <button
          type="button"
          className="btn btn--bare paso-min__lado"
          onClick={() => fijar(valor + paso)}
          disabled={valor >= max}
          aria-label={`Más: ${titulo}`}
        >
          +
        </button>
      </div>
    </div>
  );
}
